import Vue from 'vue';
import { defineStore } from 'pinia';
import _ from 'lodash';

import ProjectApi from '@/extensions/tickets/store/api/project';

export const useProjectStore = defineStore('project', {
    state: () => ({
        // Which surface opened a project's page, so Verwerfen returns there
        // instead of always landing on the project list. Same mechanism as the
        // ticket detail's origin, and kept out of the URL for the same reason.
        detailOrigin: null,
        loading: true,
        // Which list request is the current one. Two overlap on the list's very
        // first render - created() asks for the list, and one tick later the
        // default filter set applies itself and asks again - and the slower,
        // unfiltered answer used to land last and win. Same guard the ticket
        // store carries.
        projectsPaginatedRequest: 0,
        project: null,
        projectsPaginated: {
            variables: {
                order: 'name',
                direction: 'asc',
                paginationSizes: [25, 50, 100],
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0,
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: [],
        },
    }),

    actions: {
        setDetailOrigin(routeName) {
            this.detailOrigin = routeName || null;
        },

        addPaginatedEntriesToState(data) {
            this.projectsPaginated.items = data.items;
            this.projectsPaginated.pageInfo = _.cloneDeep(data.pageInfo);
            this.projectsPaginated.allColumns = _.cloneDeep(data.config.allColumns);
            this.projectsPaginated.link = _.cloneDeep(data.config.link);
            this.projectsPaginated.showFilters = _.get(data.config, 'showFilters') || false;
            this.projectsPaginated.defaultSort = _.get(data.config, 'defaultSort') || null;
        },

        defineActiveColumns(activeColumnKeys) {
            const activeColumns = [];
            _.each(this.projectsPaginated.allColumns, (column) => {
                const allowed = _.find(activeColumnKeys, (value) => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.projectsPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.projectsPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.projectsPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.projectsPaginated.filters, key);
            } else {
                Vue.set(this.projectsPaginated.filters, key, value);
            }
        },

        defineSort({ key, value }) {
            this.projectsPaginated.variables.order = key;
            this.projectsPaginated.variables.direction = value;
        },

        defineFilterVisibility(value) {
            this.projectsPaginated.showFilters = value;
        },

        updateLoading(value) {
            this.loading = value;
        },

        fetchProjectsPaginated(desk) {
            const request = ++this.projectsPaginatedRequest;

            return ProjectApi.fetchPaginated(this.projectsPaginated, desk).then((data) => {
                // A newer request went out while this one was in flight, so this
                // answer describes filters nobody is looking at any more.
                if (request !== this.projectsPaginatedRequest) return;

                this.addPaginatedEntriesToState(data);

                const activeColumnKeys = _.map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        fetchProject(id) {
            return ProjectApi.fetch(id).then((project) => {
                this.project = project;

                return project;
            });
        },

        // One page of one project's tickets. Not held in the store: it belongs
        // to the page showing it, and a second project's page must not inherit
        // the first one's rows.
        fetchProjectTickets(options) {
            return ProjectApi.fetchTicketsPaginated(options);
        },

        saveProject(id, project) {
            return id ? ProjectApi.update(id, project) : ProjectApi.create(project);
        },

        deleteProject(id) {
            return ProjectApi.delete(id);
        },
    },
});
